import React, { useState, useEffect, useRef, useMemo } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { ChevronUp } from "feather-icons-react/build/IconComponents";
import { setToogleHeader } from "../../core/redux/action";
import { useDispatch, useSelector } from "react-redux";
import { PlusCircle } from "react-feather";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import Table from "../../core/pagination/datatable";
import AddUsers from "../../core/modals/usermanagement/addusers";
import { useToast } from "../../Toaster/ToastContext";
import Loader from "../loader/loader";
import { apiClient } from "../../services/apiClient";

const Users = () => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const hasFetched = useRef(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [permissions, setPermissions] = useState({
    create: false,
    read: false,
    update: false,
    delete: false,
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 1,
    totalItems: 0,
  });

  const fetchUsers = async (pageIndex = 0, force = false) => {
    const userType = localStorage.getItem("user_type");
    const skipBusinessId = userType === "owner";
    if (hasFetched.current && !force) return;
    setLoading(true);
    try {
      const data = await apiClient.post(
        "/paginate-business-user",
        {
          pageIndex,
          pageSize: 20,
          previousPageIndex: pageIndex > 0 ? pageIndex - 1 : 0,
        },
        skipBusinessId 
      );
      if (data.success) {
        setUsers(data.data);
        setFilteredUsers(data.data);
        setPagination({
          currentPage: data.currentPage,
          pageSize: data.pageSize,
          totalPages: data.totalPages,
          totalItems: data.totalPages === 1 ? data.data.length : data.totalPages * data.pageSize,
        });
      } else {
        setUsers([]);
        setFilteredUsers([]);
        setPagination({
          currentPage: 1,
          pageSize: 20,
          totalPages: 1,
          totalItems: 0,
        });
      }
    } catch (error) {
      showToast(
        error.message || "Failed to fetch users",
        "error",
        "Business Users"
      );
    } finally {
      setLoading(false);
      hasFetched.current = true;
    }
  };
  useEffect(() => {
    fetchUsers(0);

    const storedPermissions = localStorage.getItem("rolespermissions");
    try {
      if (!storedPermissions) return;

      const Permissions = JSON.parse(storedPermissions);
      const getPermissions = JSON.parse(Permissions.permissions);

      let parsedPermissions = getPermissions.permissions || getPermissions;

      // 🔹 Find User Management section
      const userManagementSection = parsedPermissions.find(
        (item) => item.title === "User Management"
      );

      // 🔹 Find Users page
      const usersPage = userManagementSection?.childPage?.find(
        (child) => child.title === "Business Users"
      );

      if (usersPage) {
        setPermissions({
          create: usersPage.create || false,
          read: usersPage.read || false,
          update: usersPage.update || false,
          delete: usersPage.delete || false,
        });
      }
    } catch (error) {
      console.error("Error parsing permissions for Users:", error);
      showToast("Failed to load user permissions", "error", "Permissions");
    }
  }, []);

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();

    setFilteredUsers(
      users.filter((user) => {
        const name = user.name ? user.name.toLowerCase() : "";
        // const status = user.status ? user.status.toLowerCase() : "";
        const businessName = user.business?.name
          ? user.business.name.toLowerCase()
          : "";
        const branchName = user.branch?.name
          ? user.branch.name.toLowerCase()
          : "";

        return (
          name.includes(term) ||
          // status.includes(term) ||
          businessName.includes(term) ||
          branchName.includes(term)
        );
      })
    );
  };

  const handleAddUser = () => {
    setCurrentUser(null);
  };

  const handleEditUser = (user) => {
    setCurrentUser(user);
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === "active" ? "inactive" : "active";

    try {
      setLoading(true);
      const userTypeValue = localStorage.getItem("user_type");
      const businessId = localStorage.getItem("business_id") || "";
      const payload = {
        id: user.id,
        status: newStatus,
        name: user.name,
        phone: user.phone,
        email: user.email,
        role_id: user.roles.id,
        branch_id: user.branch_id,
        user_image: user.user_image,
        user_type: userTypeValue,
      };

      if (userTypeValue === "owner") {
        payload.business_id = user.business_id || businessId;
      }
      const response = await apiClient.post(
        "/store-update-business-user",
        payload
      );
      if (response.success) {
        showToast(
          `User status updated to ${newStatus}`,
          "success",
          "Business User Operation"
        );
        const updatedUsers = users.map((u) =>
          u.id === user.id ? { ...u, status: newStatus } : u
        );
        setUsers(updatedUsers);
        setFilteredUsers(updatedUsers);
      } else {
        showToast(
          response.message || "Failed to update user status",
          "error",
          "Business User Operation"
        );
      }
    } catch (error) {
      showToast(
        error.message || "Failed to update user status",
        "error",
        "Business User Operation"
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationChange = (page) => {
    setLoading(true);
    fetchUsers(page - 1, true); 
  };

  const renderCollapseTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: "User Name",
        dataIndex: "name",
        render: (text, record) => (
          <span className="userimgname" style={{ whiteSpace: "nowrap" }}>
            <ImageWithBasePath
              alt={record.name || "User Logo"}
              src={
                record.user_image === null || record.user_image === ""
                  ? "https://odabill.com/assets/img/product/noimage.png"
                  : record.user_image
              }
              className="prod_image"
            />
            <div>
              <Link to="#" className="wrap-text">
                {text}
              </Link>
            </div>
          </span>
        ),
        sorter: (a, b) => a.name.length - b.name.length,
        width: 200,
      },
      {
        title: "Business",
        dataIndex: "business",
        render: (business) => (
          <span className="wrap-text">{business ? business.name : "N/A"}</span>
        ),
        sorter: (a, b) =>
          (a.business?.name || "N/A").length -
          (b.business?.name || "N/A").length,
        width: 200,
      },
      {
        title: "Branch",
        dataIndex: "branch",
        render: (branch) => (
          <span className="wrap-text">{branch ? branch.name : "N/A"}</span>
        ),
        sorter: (a, b) =>
          (a.branch?.name || "N/A").length - (b.branch?.name || "N/A").length,
        width: 200,
      },
      {
        title: "Phone",
        dataIndex: "phone",
        render: (text) => <span className="wrap-text">{text}</span>,
        sorter: (a, b) => a.phone.length - b.phone.length,
        width: 150,
      },
      {
        title: "Email",
        dataIndex: "email",
        render: (text) => <span className="wrap-text">{text}</span>,
        sorter: (a, b) => a.email.length - b.email.length,
        width: 200,
      },
      {
        title: "Created On",
        dataIndex: "created_at",
        sorter: (a, b) => a.created_at.length - b.created_at.length,
        render: (text) => {
          const date = new Date(text);
          return isNaN(date)
            ? "N/A"
            : date
                .toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
                .split("/")
                .join("-");
        },
        width: 100,
      },
      {
        title: "Status",
        dataIndex: "status",
        render: (text, record) => (
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={text === "active"}
              onChange={() => handleToggleStatus(record)}
            />
            <span className="toggle-slider"></span>
          </label>
        ),
        sorter: (a, b) => a.status.length - b.status.length,
        width: 100,
      },
    ];

    // ✅ Only add Action column if read OR delete is true
    if (permissions.read || permissions.delete) {
      baseColumns.push({
        title: "Action",
        dataIndex: "actions",
        key: "actions",
        render: (_, record) => (
          <div className="action-table-data">
            <div className="edit-delete-action">
              {permissions.read && ( // 👈 Edit button under "read"
                <Link
                  className="me-2 p-2"
                  data-bs-toggle="modal"
                  data-bs-target="#add-units"
                  onClick={() => handleEditUser(record)}
                >
                  <i data-feather="edit" className="feather-edit" />
                </Link>
              )}
              {permissions.delete && ( // 👈 Delete button
                <Link
                  className="confirm-text p-2"
                  to="#"
                  onClick={() => showConfirmationAlert(record)}
                >
                  <i data-feather="trash-2" className="feather-trash-2" />
                </Link>
              )}
            </div>
          </div>
        ),
        width: 200,
      });
    }

    return baseColumns;
  }, [permissions]);

  const MySwal = withReactContent(Swal);

  const showConfirmationAlert = (record) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      confirmButtonText: "Yes, delete it!",
      cancelButtonColor: "#ff0000",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          const data = await apiClient.post("/delete-business-user", {
            id: record.id,
          });
          // const data = await response.json();
          if (data.success) {
            showToast(
              "User deleted successfully",
              "success",
              "Business User Operation!"
            );
            fetchUsers(0, true);
          } else {
            showToast(
              data.message || "Failed to delete user",
              "error",
              "Business User Operation!"
            );
          }
        } catch (error) {
          showToast(
            error.message || "Failed to delete user",
            "error",
            "Business User Operation!"
          );
        } finally {
          setLoading(false);
        }
      } else {
        MySwal.close();
      }
    });
  };
  return (
    <div>
      <Loader loading={loading} />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>User List</h4>
                <h6>Manage Your Users</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                  <Link
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    id="collapse-header"
                    className={data ? "active" : ""}
                    onClick={() => {
                      dispatch(setToogleHeader(!data));
                    }}
                  >
                    <ChevronUp />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
            <div className="page-btn">
              <a
                to="#"
                className="btn btn-added"
                data-bs-toggle="modal"
                data-bs-target="#add-units"
                onClick={handleAddUser}
              >
                <PlusCircle className="me-2" />
                Add New User
              </a>
            </div>
          </div>
          {/* /product list */}
          <div className="card table-list-card">
            <div className="card-body">
              <div className="table-top">
                <div className="search-set">
                  <div className="search-input">
                    <input
                      type="text"
                      placeholder="Search by user name, branch and business"
                      className="form-control form-control-sm formsearch"
                      onChange={handleSearch}
                    />
                    <Link to className="btn btn-searchset">
                      <i data-feather="search" className="feather-search" />
                    </Link>
                  </div>
                </div>
              </div>
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={filteredUsers}
                  pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    totalPages: pagination.totalPages,
                    total: pagination.totalItems, // Pass total items
                    onChange: handlePaginationChange,
                  }}
                  rowKey={(record) => record.id}
                  rowSelection={null}
                />
              </div>
            </div>
          </div>
          {/* /product list */}
        </div>
      </div>
      <AddUsers
        user={currentUser}
        onSave={() => {
          hasFetched.current = false;
          fetchUsers(0);
        }}
      />
    </div>
  );
};

export default Users;
